import React, { useState, useEffect, useMemo } from "react";
import { X, Plus, Trash2, ChevronDown } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const MaterialPickerModal = ({
  materials,
  alreadyAddedIds,
  onConfirm,
  onClose,
}) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);

  const filtered = materials.filter((m) => {
    const q = search.toLowerCase();
    return (
      m.material_name?.toLowerCase().includes(q) ||
      m.subcategory?.toLowerCase().includes(q) ||
      m.specification?.toLowerCase().includes(q) ||
      m.brand_name?.toLowerCase().includes(q)
    );
  });

  const toggle = (material) => {
    setSelected((prev) =>
      prev.find((m) => m.id === material.id)
        ? prev.filter((m) => m.id !== material.id)
        : [...prev, material],
    );
  };

  const isDisabled = (m) => alreadyAddedIds.includes(m.id);
  const isChecked = (m) => selected.some((s) => s.id === m.id);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-40 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-2xl w-[37vw] max-h-[90vh] flex flex-col shadow-xl">
        <div className="flex flex-row items-center px-3 py-2 justify-between border-b-[0.5px] border-[#A3A3A3]">
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
          >
            <X size={20} />
          </button>
          <h3 className="text-lg font-normal text-[#232323]">
            Select Material
          </h3>
          <div className="w-8" />
        </div>

        <div className="pb-2 mt-2 px-3 flex flex-col w-full items-left">
          <span className="text-sm font-normal pl-1 w-full text-[#232323]">
            Select from your material inventory
          </span>
          <div className="relative w-full flex flex-col items-left">
            <input
              autoFocus
              type="text"
              placeholder="Search Your Material"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-1 w-[97%] ml-[2px] mt-1 text-base bg-[#F3F4F6] rounded-lg border-0 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4">
          <div className="space-y-1">
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500 p-4">
                {materials.length === 0
                  ? "All materials from your library are already in the warehouse."
                  : "No materials found."}
              </p>
            )}
            {filtered.map((material) => {
              const disabled = isDisabled(material);
              const checked = isChecked(material);

              return (
                <button
                  key={material.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => !disabled && toggle(material)}
                  className={`w-full flex items-center gap-3 px-2 py-1 border-b-[0.5px] border-[#C3C3C3] text-left transition
                    ${disabled ? "cursor-not-allowed opacity-70" : checked ? "bg-purple-50" : "hover:bg-gray-50 cursor-pointer"}`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                      ${disabled ? "bg-gray-300 border-gray-300" : checked ? "bg-[#3B0069] border-[#3B0069]" : "border-[#232323]"}`}
                  >
                    {(checked || disabled) && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="white"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="font-medium text-[#232323]">
                      {material.material_name}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-auto">
                    {material?.specification && (
                      <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                        {material.specification}
                      </span>
                    )}
                    {material?.subcategory && (
                      <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                        {material.subcategory}
                      </span>
                    )}
                    {material?.uom && (
                      <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                        {material.uom}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="pt-2 px-3 border-t border-gray-100 flex items-center justify-between pb-3">
          <span className="text-xs text-gray-500">
            {selected.length > 0
              ? `${selected.length} material${selected.length > 1 ? "s" : ""} selected`
              : "Select one or more materials to add"}
          </span>
          <button
            type="button"
            disabled={selected.length === 0}
            onClick={() => onConfirm(selected)}
            className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-[#3B0069] disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-[#3B0069D9] transition-colors"
          >
            Add Selected
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateWarehouseMaterial = ({
  onClose,
  onSuccess,
  onRedirectToMaterialLibrary,
}) => {
  const [materials, setMaterials] = useState([]);
  const [warehouseMaterialIds, setWarehouseMaterialIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const { addAlert } = useAlert();

  const [organisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id,
  );

  const [lines, setLines] = useState([]);

  useEffect(() => {
    getMaterials();
  }, []);

  const getMaterials = async () => {
    try {
      setLoading(true);
      const [libraryResponse, warehouseResponse] = await Promise.allSettled([
        fetch(`${BASE_URL}/org/material-library/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ org_id: organisationId }),
        }),
        fetch(`${BASE_URL}/org/warehouse/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ org_id: organisationId }),
        }),
      ]);

      if (libraryResponse.status !== "fulfilled" || !libraryResponse.value.ok) {
        throw new Error("Failed to fetch materials");
      }

      const libraryData = await libraryResponse.value.json();
      setMaterials(Array.isArray(libraryData) ? libraryData : []);

      if (
        warehouseResponse.status === "fulfilled" &&
        warehouseResponse.value.ok
      ) {
        const warehouseData = await warehouseResponse.value.json();
        const existingIds = Array.isArray(warehouseData)
          ? [
              ...new Set(
                warehouseData
                  .map((item) => item?.material?.id)
                  .filter((id) => id !== null && id !== undefined),
              ),
            ]
          : [];
        setWarehouseMaterialIds(existingIds);
      } else {
        setWarehouseMaterialIds([]);
        console.error("Failed to fetch warehouse materials for filtering.");
      }
    } catch (err) {
      console.error("Error fetching materials:", err);
      addAlert("Failed to load materials", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePickerConfirm = (picked) => {
    const newLines = picked.map((m) => ({ material: m, currentQuantity: "" }));
    setLines((prev) => [...prev, ...newLines]);
    setShowPicker(false);
  };

  const removeLine = (index) =>
    setLines((prev) => prev.filter((_, i) => i !== index));

  const updateQty = (index, value) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === index ? { ...line, currentQuantity: value } : line,
      ),
    );

  const addedIds = lines.map((l) => l.material.id);
  const selectableMaterials = useMemo(() => {
    const existingIdsSet = new Set(warehouseMaterialIds);
    return materials.filter((material) => !existingIdsSet.has(material.id));
  }, [materials, warehouseMaterialIds]);
  const hasSelectableMaterials = selectableMaterials.length > 0;

  const createWarehouseMaterials = async () => {
    if (lines.length === 0) {
      addAlert("Please add at least one material", "error");
      return;
    }
    if (lines.some((l) => !l.currentQuantity)) {
      addAlert("Please enter quantity for all materials", "error");
      return;
    }

    try {
      setSubmitting(true);
      const results = await Promise.all(
        lines.map((line) =>
          fetch(`${BASE_URL}/org/warehouse/create/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({
              organization: organisationId,
              material: line.material.id,
              current_quantity: line.currentQuantity,
            }),
          }).then(async (res) => {
            if (!res.ok) {
              const err = await res.json();
              throw new Error(
                err.error || `Failed to add ${line.material.material_name}`,
              );
            }
            return res.json();
          }),
        ),
      );
      addAlert(
        `${results.length} material${results.length > 1 ? "s" : ""} added to warehouse successfully`,
        "success",
      );
      onSuccess?.(results);
      onClose();
    } catch (err) {
      addAlert(err.message || "Failed to create warehouse material", "error");
      console.error("Error creating warehouse material:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showPicker && !loading && (
        <MaterialPickerModal
          materials={selectableMaterials}
          alreadyAddedIds={addedIds}
          onConfirm={handlePickerConfirm}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl w-[48vw] max-h-[90vh] flex flex-col shadow-xl">
          <div className="relative flex items-center justify-center px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#232323]">
              Add Material to Warehouse
            </h2>
            <button
              onClick={onClose}
              className="absolute right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <p className="text-sm text-gray-500 mb-5">
              Can't find your material?{" "}
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  onRedirectToMaterialLibrary?.();
                }}
                className="text-[#3B0069] underline font-medium"
              >
                Add here
              </button>
            </p>

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#232323]">
                  Materials
                </span>
                {lines.length > 0 && (
                  <span className="min-w-[22px] h-[22px] flex items-center justify-center rounded-full bg-[#DDD0F0] text-[#3B0069] text-xs font-sm px-1.5">
                    {lines.length}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                disabled={loading || !hasSelectableMaterials}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EDE3F6] text-[#3B0069] hover:bg-[#DDD0F0] transition-colors disabled:opacity-40"
                title={
                  hasSelectableMaterials
                    ? "Add materials"
                    : "All materials are already in warehouse"
                }
              >
                <Plus size={18} />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B0069]" />
                <span className="ml-2 text-sm text-gray-500">
                  Loading materials…
                </span>
              </div>
            ) : lines.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#3B0069] hover:bg-[#FAF8FF] transition-colors"
                onClick={() => setShowPicker(true)}
              >
                <div className="w-10 h-10 rounded-full bg-[#EDE3F6] flex items-center justify-center mb-3">
                  <Plus size={20} className="text-[#3B0069]" />
                </div>
                <p className="text-sm font-medium text-gray-600">
                  Add materials
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Click to select from your inventory
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {lines.map((line, index) => (
                  <div
                    key={line.material.id}
                    className="flex justify-start w-full gap-3"
                  >
                    {/* <button
                      type="button"
                      onClick={() => setShowPicker(true)}
                      className="flex flex-row cursor-pointer items-center w-[42%] justify-between px-4 py-2 rounded-lg bg-[#F3F4F6]"
                    >
                      <span className="text-left text-[#232323] font-normal focus:outline-none overflow-hidden text-ellipsis whitespace-nowrap">
                        {line.material.material_name}
                      </span>
                      <ChevronDown size={16} className="text-gray-600 shrink-0 ml-2" />
                    </button> */}
                    <div className="flex flex-row items-center w-[42%] justify-between px-4 py-2 rounded-lg bg-[#F3F4F6]">
                      <span className="text-left text-[#232323] font-normal overflow-hidden text-ellipsis whitespace-nowrap">
                        {line.material.material_name}
                      </span>
                    </div>

                    <div className="flex flex-row items-center w-[48%] justify-between px-3 py-2 rounded-lg bg-[#F3F4F6] gap-2">
                      <input
                        type="number"
                        placeholder="Qty"
                        value={line.currentQuantity}
                        onChange={(e) => updateQty(index, e.target.value)}
                        min="0"
                        step="any"
                        className="w-[50%] text-center bg-transparent text-[#232323] font-normal focus:outline-none"
                      />
                      {line.material.uom && (
                        <>
                          <div className="w-px h-5 bg-gray-300 shrink-0" />
                          <span className="px-2 text-[#232323] shrink-0 whitespace-nowrap font-normal text-sm">
                            {line.material.uom}
                          </span>
                        </>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeLine(index)}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={createWarehouseMaterials}
              disabled={
                submitting ||
                lines.length === 0 ||
                lines.some((l) => !l.currentQuantity)
              }
              className="px-6 py-2.5 text-sm bg-[#3B0069] text-white font-semibold rounded-lg hover:bg-[#2d0052] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {submitting
                ? "Adding…"
                : `Add Material${lines.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateWarehouseMaterial;
